import React from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { MagicStar } from 'iconsax-react';
import dayjs from 'dayjs';
import CustomTable from '@/components/atoms/CustomTable';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import { ShipmentFeedbackItem } from '@/types/logisticsFeedback';

interface LogisticsFeedbackTableProps {
  data: ShipmentFeedbackItem[];
  currentPage: number;
  pageSize: number;
  totalElements: number;
  loading?: boolean;
  onPageChange: (page: number, pageSize?: number) => void;
  onSelectFeedback: (item: ShipmentFeedbackItem) => void;
  'data-testid'?: string;
}

const headerClassName =
  '!font-semibold !text-base !text-light-primary dark:!text-dark-primary !leading-none';

const renderCenteredPlaceholder = (testId: string) => (
  <div className="flex justify-center items-center w-full" data-testid={testId}>
    <InfoText
      text="-"
      className="!text-sm !font-normal !text-light-text-secondary dark:!text-dark-text-secondary"
    />
  </div>
);

export const LogisticsFeedbackTable: React.FC<LogisticsFeedbackTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalElements,
  loading = false,
  onPageChange,
  onSelectFeedback,
  'data-testid': dataTestId = 'logistics-feedback-table',
}) => {
  const { t, i18n } = useTranslation();

  const columns: ColumnsType<ShipmentFeedbackItem> = [
    {
      title: (
        <TitleText
          text={t('logisticsFeedback.orderIdColumn')}
          className={headerClassName}
          data-testid="logistics-feedback-col-order-id"
        />
      ),
      dataIndex: 'orderSerial',
      key: 'orderSerial',
      render: (serial: string | undefined, record) => {
        if (!serial) {
          return renderCenteredPlaceholder(`logistics-feedback-empty-order-${record.id}`);
        }
        return (
          <button
            type="button"
            onClick={() => onSelectFeedback(record)}
            className="!font-normal !text-sm text-light-primary dark:text-dark-primary hover:text-sky-600 dark:hover:text-sky-400 transition-colors cursor-pointer text-left"
            data-testid={`logistics-feedback-row-order-id-${record.id}`}
          >
            {serial}
          </button>
        );
      },
    },
    {
      title: (
        <TitleText
          text={t('logisticsFeedback.companyNameColumn')}
          className={headerClassName}
          data-testid="logistics-feedback-col-company-name"
        />
      ),
      dataIndex: 'logisticsCompanyName',
      key: 'logisticsCompanyName',
      render: (name: string | null | undefined, record) => {
        if (!name) {
          return renderCenteredPlaceholder(`logistics-feedback-empty-company-${record.id}`);
        }
        return (
          <InfoText
            text={name}
            className="!text-sm !font-normal !text-light-primary dark:!text-dark-primary"
            data-testid={`logistics-feedback-company-${record.id}`}
          />
        );
      },
    },
    {
      title: (
        <TitleText
          text={t('logisticsFeedback.buyerNameColumn')}
          className={headerClassName}
          data-testid="logistics-feedback-col-buyer-name"
        />
      ),
      dataIndex: 'requesterProfileName',
      key: 'requesterProfileName',
      render: (buyer: string | null | undefined, record) => {
        if (!buyer) {
          return renderCenteredPlaceholder(`logistics-feedback-empty-buyer-${record.id}`);
        }
        return (
          <InfoText
            text={buyer}
            className="!text-sm !font-normal !text-light-primary dark:!text-dark-primary"
            data-testid={`logistics-feedback-buyer-${record.id}`}
          />
        );
      },
    },
    {
      title: (
        <TitleText
          text={t('logisticsFeedback.feedbackColumn')}
          className={headerClassName}
          data-testid="logistics-feedback-col-feedback"
        />
      ),
      dataIndex: 'requesterFeedback',
      key: 'requesterFeedback',
      render: (rating: number | undefined, record) => {
        if (typeof rating !== 'number' || rating === 0) {
          return renderCenteredPlaceholder(`logistics-feedback-empty-rating-${record.id}`);
        }
        return (
          <div
            className="flex items-center gap-1.5"
            data-testid={`logistics-feedback-rating-cell-${record.id}`}
          >
            <InfoText
              text={rating.toFixed(1)}
              className="!text-sm !font-normal !text-light-primary dark:!text-dark-primary"
            />
            <MagicStar
              size={22}
              variant="Bold"
              className="text-amber-400 fill-amber-400 shrink-0"
            />
          </div>
        );
      },
    },
    {
      title: (
        <TitleText
          text={t('logisticsFeedback.dateCreatedColumn')}
          className={headerClassName}
          data-testid="logistics-feedback-col-date-created"
        />
      ),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (date: string | undefined, record) => {
        if (!date) {
          return renderCenteredPlaceholder(`logistics-feedback-empty-date-${record.id}`);
        }
        const parsedDate = dayjs(date);
        const formattedDate = parsedDate.isValid()
          ? parsedDate.locale(i18n.language).format('DD MMM YYYY')
          : date;
        return (
          <InfoText
            text={formattedDate}
            className="!text-sm !text-light-text-secondary dark:!text-dark-text-secondary"
            data-testid={`logistics-feedback-date-${record.id}`}
          />
        );
      },
    },
    {
      title: (
        <TitleText
          text={t('logisticsFeedback.typeColumn')}
          className={headerClassName}
          data-testid="logistics-feedback-col-type"
        />
      ),
      dataIndex: 'requestType',
      key: 'requestType',
      render: (_: unknown, record) => {
        const typeCode = record.requestType?.code;
        const typeName = record.requestType?.name;
        const label =
          typeCode === 'BIDDING'
            ? t('logisticsFeedback.typeBidding')
            : typeCode === 'MARKETPLACE' || typeCode === 'STORE'
            ? t('logisticsFeedback.typeMarketplace')
            : typeName;

        if (!label) {
          return renderCenteredPlaceholder(`logistics-feedback-empty-type-${record.id}`);
        }

        return (
          <InfoText
            text={label}
            className="!text-sm !font-normal !leading-[22px] !text-[#637381] dark:!text-dark-text-secondary"
            data-testid={`logistics-feedback-type-${record.id}`}
          />
        );
      },
    },
  ];

  return (
    <div data-testid={dataTestId}>
      <CustomTable<ShipmentFeedbackItem>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={totalElements}
        onPageChange={onPageChange}
        onRowClick={(_, record) => onSelectFeedback(record)}
        className="[&_.ant-table-row]:cursor-pointer [&_.ant-table-tbody_td]:!text-sm"
        data-testid={`${dataTestId}-custom-table`}
      />
    </div>
  );
};
